import { useCallback, useMemo, useRef } from 'react';

import {
  btag,
  constants,
  NotificationManager,
  PaymentV2Api,
  pushToDataLayer,
  SessionStorageManager,
} from '@atlas/core';
import { onDepositSubmit } from '@atlas/react-forms';

import {
  ACCOUNT_NUMBERS_POPUP_TYPES,
  bonusStatuses,
  DATA_LAYER_STORAGE_KEYS,
} from 'common/constants';
import { useDepositPostHog } from 'hooks/usePostHog';
import { useDepositBonusContext } from 'src/components/ClientProfile/Payment/Deposit/DepositBonusContext';
import {
  buildPaymentUrl,
  showAlternativeMethodsOrError,
} from 'src/common/Popup/alternativePaymentUtils';
import { depositSubmit } from 'src/helpers/depositSubmit';

export const useDepositSubmit = ({
  account,
  backUrl,
  browser,
  currency,
  ip,
  isAccountBlocked,
  isCashboxDepositClicked,
  locale,
  os,
  paymentMethod,
  push,
  ref,
  setPaymentUrl,
  showSelfRegulationPopup,
  showSelfRegulationPopupIfNeeded,
  showSpentLimitPopup,
  showSpentLimitPopupIfNeeded,
  wallet,
}) => {
  const loadingNotificationId = useRef();
  const { handleDepositStart, handleDepositSuccess } = useDepositPostHog();
  const { depositAmount, intendedBonusRef, selectedBonus } = useDepositBonusContext();

  const onCreateDeposit = useCallback(() => {
    ref.current?.setIsLoading?.(false);
    loadingNotificationId.current = NotificationManager.display({
      data: {
        headerTitle: 'deposit_loading',
        text: 'deposit_loading_text',
        title: 'deposit_loading_title',
      },
      type: 'paymentLoading',
    });
  }, [ref]);

  const onDepositStatusError = useCallback(
    error => {
      NotificationManager.removeNotification(loadingNotificationId.current);

      const fallbackError = () => {
        NotificationManager.display({
          data: {
            className: 'deposit-failed-popup',
            headerTitle: 'deposit',
            text: error?.code ? error.code.toLowerCase() : 'deposit_failed_message',
            title: 'deposit_failed_title',
          },
          type: 'errorTransaction',
        });
      };

      if (!paymentMethod?.alternativePmEnabled) {
        fallbackError();
        return;
      }

      showAlternativeMethodsOrError({
        fallbackError,
        onSelectMethod: method =>
          push(buildPaymentUrl(`${backUrl}/${method.id}`, depositAmount, method)),
        onViewAll: () => push(buildPaymentUrl(backUrl, depositAmount, null)),
        operationType: 'DEPOSIT',
        pmId: paymentMethod.id,
      });
    },
    [backUrl, depositAmount, paymentMethod, push],
  );

  const submitHandler = useCallback(
    data => {
      depositSubmit(data, paymentMethod, setPaymentUrl, depositAmount);
    },
    [paymentMethod, setPaymentUrl, depositAmount],
  );

  const performPaymentRequest = useCallback(
    async paymentOptions => {
      const isBonusInstance = selectedBonus?.status === bonusStatuses.ACTIVATED;
      const resolvedBonusId = isBonusInstance ? selectedBonus?.bonus?.id : selectedBonus?.id;
      const bonusId = selectedBonus ? resolvedBonusId : null;

      handleDepositStart({
        amount: paymentOptions.amount,
        bonus_id: bonusId,
        payment_method: paymentMethod?.name ?? null,
      });

      const res = await PaymentV2Api.getPaymentData({
        onCreatePayment: onCreateDeposit,
        onPaymentStatusError: onDepositStatusError,
        paymentOptions: {
          ...paymentOptions,
          currency,
          ...(selectedBonus &&
            !selectedBonus.disabled &&
            !intendedBonusRef.current && {
              additionalAttributes: {
                bonusId: isBonusInstance ? selectedBonus?.bonus?.id : selectedBonus.id,
                bonusInstanceId: isBonusInstance ? selectedBonus.id : null,
              },
            }),
        },
        paymentType: PaymentV2Api.PaymentType.DEPOSIT,
      });

      NotificationManager.removeNotification(loadingNotificationId.current);
      pushToDataLayer(
        'deposit_created',
        {
          amount: paymentOptions.amount,
          client: { id: account.id },
          created_at: Date.now(),
          currency: wallet.currency,
          payment_method: res.paymentName,
        },
        btag,
      );

      handleDepositSuccess({
        transaction_id: res?.publicPaymentId ?? res?.paymentId ?? null,
      });

      return res;
    },
    [
      account.id,
      currency,
      handleDepositStart,
      handleDepositSuccess,
      intendedBonusRef,
      onCreateDeposit,
      onDepositStatusError,
      paymentMethod?.name,
      selectedBonus,
      wallet.currency,
    ],
  );

  const checkLimits = useCallback(async () => {
    if (isAccountBlocked) {
      NotificationManager.display({ id: 'accountBlocked', type: 'accountBlocked' });
      return false;
    }
    if (showSpentLimitPopup) {
      await showSpentLimitPopupIfNeeded();
      return false;
    }
    if (showSelfRegulationPopup) {
      await showSelfRegulationPopupIfNeeded();
      return false;
    }
    return true;
  }, [
    isAccountBlocked,
    showSpentLimitPopup,
    showSelfRegulationPopup,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
  ]);

  const action = useMemo(() => {
    if (paymentMethod?.displayAccountNumberSelector) {
      return async formData => {
        if (!(await checkLimits())) return;
        NotificationManager.display({
          data: {
            amount: formData.amount,
            onDepositStatusError,
            paymentAction: performPaymentRequest,
            paymentMethod,
            paymentSubmit: submitHandler,
            pmId: formData.pmId,
            type: ACCOUNT_NUMBERS_POPUP_TYPES.DEPOSIT,
          },
          message: 'accountNumbers',
          type: 'accountNumbers',
        });
      };
    }

    return onDepositSubmit(async paymentOptions => {
      if (!(await checkLimits())) return;
      ref.current?.setIsLoading?.(true);
      const res = await performPaymentRequest(paymentOptions);

      if (!isCashboxDepositClicked) {
        pushToDataLayer(
          'cashbox_deposit_click',
          {
            OS: os,
            amount: paymentOptions.amount,
            browser,
            created_at: Date.now(),
            device: constants.DEVICE_TYPES.MOBILE,
            ip,
            locale,
            page_referer: window.location.pathname,
            payment_method: paymentMethod?.name,
            user_id: account.id,
          },
          btag,
        );
        SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.CASHBOX_DEPOSIT_CLICKED, true);
      }
      return res;
    });
  }, [
    account.id,
    browser,
    checkLimits,
    ip,
    isCashboxDepositClicked,
    locale,
    os,
    paymentMethod,
    performPaymentRequest,
    ref,
    submitHandler,
    onDepositStatusError,
  ]);

  return { action, submitHandler };
};
